import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { EmptyState } from './EmptyState';
import { Loading } from './Loading';
import { ErrorAlert } from './ErrorAlert';
import { apiFetch } from '@/lib/api';
import { Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

interface Trade {
  id: number;
  ticker: string;
  price: number;
  vol: number;
  direction: 'BUY' | 'SELL';
  cost_after?: number;
  pos_after?: number;
  realized_pnl_after?: number;
  created_at: string;
}

interface EditForm {
  ticker: string;
  price: string;
  vol: string;
  direction: 'BUY' | 'SELL';
  tradeDate: string;
  tradeTime: string;
}

const TradeList: React.FC = () => {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    ticker: '', price: '', vol: '', direction: 'BUY', tradeDate: '', tradeTime: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  const loadTrades = () => {
    setLoading(true);
    apiFetch('/api/trades')
      .then((r) => r.json())
      .then((data) => {
        setTrades(data);
        setLoading(false);
      })
      .catch(() => {
        setError('无法加载数据');
        setLoading(false);
      });
  };

  useEffect(() => {
    loadTrades();
    const timer = setInterval(loadTrades, 10000); // auto-refresh every 10s
    return () => clearInterval(timer);
  }, []);

  const startEdit = (trade: Trade) => {
    setEditingId(trade.id);
    const dt = new Date(trade.created_at);
    const dateStr = isNaN(dt.getTime()) ? trade.created_at.split('T')[0] : dt.toISOString().split('T')[0];
    const timeStr = isNaN(dt.getTime()) ? '09:30' : dt.toTimeString().slice(0, 5);
    setEditForm({
      ticker: trade.ticker,
      price: String(trade.price),
      vol: String(trade.vol),
      direction: trade.direction,
      tradeDate: dateStr,
      tradeTime: timeStr,
    });
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;

    const price = parseFloat(editForm.price);
    const vol = parseInt(editForm.vol);
    if (!price || !vol || vol <= 0) {
      setError('价格和数量必须有效');
      return;
    }

    setSaving(true);
    const createdAt = `${editForm.tradeDate}T${editForm.tradeTime}:00`;
    try {
      const r = await apiFetch(`/api/trades/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticker: editForm.ticker,
          price,
          vol,
          direction: editForm.direction,
          created_at: createdAt,
        }),
      });
      if (!r.ok) {
        const e = await r.json();
        setError(e.error || '更新失败');
        setSaving(false);
        return;
      }
      setEditingId(null);
      setSaving(false);
      loadTrades();
    } catch {
      setError('网络错误，更新失败');
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await apiFetch(`/api/trades/${deleteTarget}`, { method: 'DELETE' });
      if (!r.ok) {
        setError('删除失败');
        setDeleteTarget(null);
        return;
      }
      setDeleteTarget(null);
      loadTrades();
    } catch {
      setError('网络错误，删除失败');
      setDeleteTarget(null);
    }
  };

  const handleMove = async (id: number, direction: 'up' | 'down') => {
    try {
      const r = await apiFetch(`/api/trades/${id}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ direction }),
      });
      if (!r.ok) {
        const e = await r.json();
        setError(e.error || '调整顺序失败');
        return;
      }
      loadTrades();
    } catch {
      setError('网络错误，调整顺序失败');
    }
  };

  // Group trades by ticker and sort chronologically
  const grouped: Record<string, Trade[]> = {};
  for (const t of trades) {
    if (!grouped[t.ticker]) grouped[t.ticker] = [];
    grouped[t.ticker].push(t);
  }
  for (const ticker of Object.keys(grouped)) {
    grouped[ticker].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <Loading />
        </CardContent>
      </Card>
    );
  }

  if (error && trades.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          <ErrorAlert>{error}</ErrorAlert>
          <button onClick={loadTrades} className="block mx-auto text-sm text-[#533afd] hover:underline">
            重试
          </button>
        </CardContent>
      </Card>
    );
  }

  if (trades.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>交易记录</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            title="暂无交易记录"
            description="请在「做T助手」页面创建交易"
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>交易记录</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('/api/trades/export', '_blank')}
              className="text-xs"
            >
              <Download className="h-3.5 w-3.5 mr-1" /> 导出 CSV
            </Button>
            <span className="text-xs text-[#64748d] tabular-nums">{trades.length} 笔</span>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <ErrorAlert onClose={() => setError('')} className="mb-3 text-xs">
              {error}
            </ErrorAlert>
          )}

          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {Object.entries(grouped).map(([ticker, tickerTrades]) => (
              <div key={ticker}>
                <div className="flex items-center gap-2 text-sm font-medium text-[#061b31] bg-[#f6f9fc] px-3 py-1.5 rounded-[4px] mb-1">
                  <span className="font-mono">{ticker}</span>
                  <Badge variant="outline" className="text-[10px]">{tickerTrades.length} 笔</Badge>
                </div>

                {tickerTrades.map((trade) => (
                  <div key={trade.id}>
                    {editingId === trade.id ? (
                      /* Edit mode */
                      <div className="border border-[#533afd]/30 rounded-[6px] p-3 mb-2 bg-[#533afd]/[0.02]">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <label className="text-xs text-[#64748d] mb-1 block">代码</label>
                            <Input
                              value={editForm.ticker}
                              onChange={e => setEditForm(f => ({ ...f, ticker: e.target.value }))}
                              className="h-8 font-mono text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-[#64748d] mb-1 block">方向</label>
                            <select
                              value={editForm.direction}
                              onChange={e => setEditForm(f => ({ ...f, direction: e.target.value as 'BUY' | 'SELL' }))}
                              className="w-full h-8 px-2 border border-input rounded-md text-sm bg-transparent"
                            >
                              <option value="BUY">买入</option>
                              <option value="SELL">卖出</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-[#64748d] mb-1 block">成交价格</label>
                            <Input
                              type="number" step="0.01"
                              value={editForm.price}
                              onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))}
                              className="h-8 font-mono text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-[#64748d] mb-1 block">数量</label>
                            <Input
                              type="number" step="100"
                              value={editForm.vol}
                              onChange={e => setEditForm(f => ({ ...f, vol: e.target.value }))}
                              className="h-8 font-mono text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-[#64748d] mb-1 block">交易日期</label>
                            <Input
                              type="date"
                              value={editForm.tradeDate}
                              onChange={e => setEditForm(f => ({ ...f, tradeDate: e.target.value }))}
                              className="h-8 font-mono text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-[#64748d] mb-1 block">交易时间</label>
                            <Input
                              type="time"
                              value={editForm.tradeTime}
                              onChange={e => setEditForm(f => ({ ...f, tradeTime: e.target.value }))}
                              className="h-8 font-mono text-sm"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" onClick={saveEdit} disabled={saving}>
                            {saving ? '保存中...' : '保存'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit}>
                            取消
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View mode */
                      <div className="border border-[#e5edf5] rounded-[6px] p-3 mb-2 hover:bg-[#fafbfd] transition-colors">
                        <div className="flex justify-between items-center">
                          <Badge variant={trade.direction === 'BUY' ? 'default' : 'destructive'}>
                            {trade.direction === 'BUY' ? '买入' : '卖出'}
                          </Badge>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleMove(trade.id, 'up')}
                              className="text-xs text-[#64748d] hover:text-[#061b31] hover:underline"
                              title="上移"
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => handleMove(trade.id, 'down')}
                              className="text-xs text-[#64748d] hover:text-[#061b31] hover:underline"
                              title="下移"
                            >
                              ↓
                            </button>
                            <button
                              onClick={() => startEdit(trade)}
                              className="text-xs text-[#533afd] hover:underline"
                            >
                              编辑
                            </button>
                            <button
                              onClick={() => setDeleteTarget(trade.id)}
                              className="text-xs text-[#ea2261] hover:underline"
                            >
                              删除
                            </button>
                          </div>
                        </div>
                        <div className="text-sm text-[#64748d] mt-1 tabular-nums">
                          {trade.vol} 股 x {trade.price.toFixed(2)}
                          <span className="ml-3 font-medium text-[#061b31]">
                            ¥{(trade.vol * trade.price).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-1 text-xs text-[#64748d]">
                          <span>
                            {trade.created_at?.replace('T', ' ').substring(0, 16)}
                            {trade.pos_after !== undefined && (
                              <span className="ml-3 text-[#533afd] font-medium">持仓: {trade.pos_after}</span>
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除</DialogTitle>
            <DialogDescription>
              确定要删除这笔交易吗？此操作不可恢复，且会重新计算该股票的所有持仓和盈亏数据。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>取消</Button>
            <Button variant="destructive" onClick={confirmDelete}>确认删除</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TradeList;
